/**
 * Script: Bonsai search hooks (bonsai-search)
 * -----------------------------------------------------------------------------
 * Global hooks for the <bonsai-search> web component, loaded site-wide when
 * Bonsai search is enabled. The component resolves these from `window` via its
 * `on-price` and `on-filter-panel-render` attributes; the title cleanup runs
 * off the component's composed `results` events.
 *
 */
(function () {
  if (window.bonsaiSearchPrice) {
    return
  }

  var RESULTS_WINDOW = 50

  /**
   * Shopper-facing copy, set by the search drawer from the theme's locales.
   * - Falls back to English so the component still reads correctly if the
   *   asset runs before the drawer has mounted.
   */
  var FALLBACK_LABELS = {
    apply: 'Apply',
    clear: 'Clear',
    close: 'Close',
    filter: 'Filter by',
    filterTitle: 'Filter',
    quickView: 'Quick View',
    sort: 'Sort by',
    sortOptions: {
      price_asc: 'Price, low to high',
      price_desc: 'Price, high to low',
      relevance: 'Relevance',
    },
    sortTitle: 'Sort',
  }

  /**
   * Read one label, falling back to English.
   * @param {String} name - Label name.
   * @returns {String}
   */
  function label(name) {
    var labels = window.bonsaiSearchLabels || {}
    return labels[name] || FALLBACK_LABELS[name]
  }

  /**
   * Read one sort option's label, falling back to English.
   * @param {String} value - Sort option value.
   * @returns {String}
   */
  function sortLabel(value) {
    var labels = (window.bonsaiSearchLabels || {}).sortOptions || {}
    return labels[value] || FALLBACK_LABELS.sortOptions[value]
  }

  var observedContainers = new WeakSet()
  var paintedSearches = new WeakSet()
  var observedDropdowns = new WeakSet()
  var containerBadges = new WeakMap()
  var containerWindows = new WeakMap()
  var windowObservers = new WeakMap()
  var filterRoots = []
  var pendingTriggerFocus = null

  /**
   * Format a result price to match the theme, e.g. $1,995 / CAD $2,617.
   * - Formatted by the drawer where it has mounted, as only it can reach the
   *   store's money format and the market's currency.
   * @param {Object} result - Bonsai search result.
   * @param {String} price - Raw price string, e.g. "1995.00".
   * @returns {String}
   */
  window.bonsaiSearchPrice = function (result, price) {
    var formatted = window.bonsaiSearchFormatPrice && window.bonsaiSearchFormatPrice(price)

    if (formatted) {
      return formatted
    }

    var number = Number(price)

    if (!Number.isFinite(number)) {
      return price || ''
    }

    var hasCents = Math.round(number * 100) % 100 !== 0

    return '$' + number.toLocaleString('en-US', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    })
  }

  /**
   * Shorten result titles to match product cards by stripping the "|SKU"
   * suffix, e.g. "Naga Wrap, Sterling Silver, 2.5MM|BUS603421BSP".
   * @param {Element} container - The component's results container.
   */
  function cleanTitles(container) {
    container.querySelectorAll('[class*="bonsai-result-title"]').forEach(function (title) {
      var text = title.textContent || ''
      var index = text.indexOf('|')

      if (index > -1) {
        title.textContent = text.slice(0, index).trim()
      }
    })
  }

  /**
   * Enhance result cards to match product cards: shortened titles, same-tab
   * navigation, merchandising badges, a quick view button opening the
   * theme's quick add drawer, and the Wishlist King heart.
   * @param {Element} container - The component's results container.
   */
  function enhanceCards(container) {
    cleanTitles(container)
    ensureCardStyles(container.getRootNode())

    var badges = containerBadges.get(container) || {}

    container.querySelectorAll('[class*="bonsai-result-card"]').forEach(function (card) {
      if (card.tagName === 'A') {
        card.target = '_self'
      }

      var wrapper = card.querySelector('[class*="bonsai-result-image-wrapper"]')

      if (!wrapper) {
        return
      }

      var badge = badges[getCardPath(card)]

      if (badge && !wrapper.querySelector('.jh-badge')) {
        wrapper.appendChild(buildBadge(badge))
      }

      if (!wrapper.querySelector('.jh-quick-view')) {
        wrapper.appendChild(buildQuickView(card))
      }

      if (window.WishlistKing && !wrapper.querySelector('wishlist-button-collection')) {
        var heart = buildWishlistButton(card)

        if (heart) {
          wrapper.appendChild(heart)
        }
      }
    })

    applyResultsWindow(container)
  }

  /**
   * Window the rendered cards so only the first batch shows, growing by a
   * batch each time the shopper scrolls near the last visible card.
   * @param {Element} container - The component's results container.
   */
  function applyResultsWindow(container) {
    var cards = container.querySelectorAll('[class*="bonsai-result-card"]')
    var count = containerWindows.get(container) || RESULTS_WINDOW
    var lastVisible = null

    cards.forEach(function (card, index) {
      card.classList.toggle('jh-card-hidden', index >= count)

      if (index < count) {
        lastVisible = card
      }
    })

    observeLastCard(container, cards.length > count ? lastVisible : null)
  }

  /**
   * Reveal the next batch of cards when the last visible card comes within
   * a viewport and a half of view.
   * @param {Element} container - The component's results container.
   * @param {Element} [card] - Last visible card, or null when all cards show.
   */
  function observeLastCard(container, card) {
    var observer = windowObservers.get(container)

    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        var isNear = entries.some(function (entry) {
          return entry.isIntersecting
        })

        if (!isNear) {
          return
        }

        var count = containerWindows.get(container) || RESULTS_WINDOW
        containerWindows.set(container, count + RESULTS_WINDOW)
        applyResultsWindow(container)
      }, { rootMargin: '150% 0px' })

      windowObservers.set(container, observer)
    }

    observer.disconnect()

    if (card) {
      observer.observe(card)
    }
  }

  /**
   * Map result URL paths to their merchandising badge, read from the same
   * `badge:` product tags as the product-card component.
   * @param {Object[]} results - Bonsai search results.
   * @returns {Object}
   */
  function buildBadgeMap(results) {
    var badges = {}

    results.forEach(function (result) {
      var badge = (result.tags || []).filter(function (tag) {
        return tag.includes('badge:')
      })[0]

      if (!badge || !result.publicUrl) {
        return
      }

      var path = getCardPath({ href: result.publicUrl })

      if (path) {
        badges[path] = badge.replace('badge:', '').trim()
      }
    })

    return badges
  }

  /**
   * Build the merchandising badge for one result card.
   * @param {String} label - Badge label from the product's `badge:` tag.
   * @returns {Element}
   */
  function buildBadge(label) {
    var badge = document.createElement('span')
    badge.className = 'jh-badge'
    badge.textContent = label
    return badge
  }

  /**
   * Pathname of a result card's product URL.
   * @param {Object} card - Result card anchor, or any object with an href.
   * @returns {String}
   */
  function getCardPath(card) {
    try {
      return new URL(card.href, window.location.origin).pathname
    } catch (error) {
      return ''
    }
  }

  /**
   * Build the quick view trigger for one result card.
   * @param {Element} card - Result card anchor.
   * @returns {Element}
   */
  function buildQuickView(card) {
    var trigger = label('quickView')

    var button = document.createElement('button')
    button.type = 'button'
    button.className = 'jh-quick-view'
    button.setAttribute('aria-label', trigger)

    var text = document.createElement('span')
    text.className = 'jh-quick-view__label'
    text.textContent = trigger

    var plus = document.createElement('span')
    plus.className = 'jh-quick-view__plus'
    plus.textContent = '+'

    button.appendChild(text)
    button.appendChild(plus)

    button.addEventListener('click', function (event) {
      event.preventDefault()
      event.stopPropagation()

      var path = getCardPath(card)

      if (!path) {
        return
      }

      var segments = path.split('/').filter(Boolean)
      button.dispatchEvent(new CustomEvent('bonsai:quick-view', {
        bubbles: true,
        composed: true,
        detail: {
          handle: segments[segments.length - 1] || '',
          trigger: button,
          url: card.href,
        },
      }))
    })

    return button
  }

  /**
   * Build the Wishlist King heart for one result card.
   * - The app injects its own buttons by watching the document, which cannot
   *   see into the closed shadow root, so the element is created here and
   *   the app's custom element upgrades it where it lands.
   * - The click's default is prevented as the card is an anchor, which would
   *   otherwise navigate as the heart is toggled.
   * @param {Element} card - Result card anchor.
   * @returns {Element|null}
   */
  function buildWishlistButton(card) {
    var path = getCardPath(card)
    var handle = path && path.split('/').filter(Boolean).pop()

    if (!handle) {
      return null
    }

    var heart = document.createElement('wishlist-button-collection')
    heart.setAttribute('data-product-handle', handle)
    heart.setAttribute('show-icon', '')
    heart.setAttribute('icon', 'wishlist')

    heart.addEventListener('click', function (event) {
      event.preventDefault()
    })

    return heart
  }

  /**
   * Rebuild the wishlist hearts when the page returns from the back-forward
   * cache, as the restored page keeps the hearts frozen at their old state
   * while the wishlist may have changed on the page navigated to; the app
   * refreshes its own buttons but cannot see into the closed shadow root.
   * Freshly created elements upgrade against the current wishlist.
   */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted || !window.WishlistKing) {
      return
    }

    filterRoots.forEach(function (root) {
      root.querySelectorAll('wishlist-button-collection').forEach(function (heart) {
        heart.remove()
      })

      root.querySelectorAll('[class*="bonsai-result-card"]').forEach(function (card) {
        var wrapper = card.querySelector('[class*="bonsai-result-image-wrapper"]')
        var heart = wrapper && buildWishlistButton(card)

        if (heart) {
          wrapper.appendChild(heart)
        }
      })
    })
  })

  /**
   * Card styles, injected once per shadow root.
   * - Cards render visible immediately as the SDK's staggered fade-in would
   *   leave late batches of a windowed result set invisible for seconds.
   * - Hidden cards sit beyond the current scroll window.
   * - Badge: merchandising badge matching the product-card badge. Its colour is
   *   set explicitly as the card is an anchor and the badge would otherwise
   *   inherit the browser's link colour inside the shadow root.
   * - Quick view desktop: theme quick add bar shown on card hover.
   * - Quick view mobile: theme quick add plus button, always visible.
   * @param {Node} root - The component's shadow root.
   */
  function ensureCardStyles(root) {
    if (!root || !root.host || root.bonsaiJhCardStyles) {
      return
    }

    root.bonsaiJhCardStyles = true
    var style = document.createElement('style')
    style.textContent = [
      '[class*="bonsai-result-card"] {',
      '  opacity: 1;',
      '  transform: none;',
      '}',
      '.jh-card-hidden {',
      '  display: none;',
      '}',
      '[class*="bonsai-result-image-wrapper"] {',
      '  position: relative;',
      '}',
      '.jh-badge {',
      '  color: var(--color-neutral-dark-grey, #646464);',
      '  font-family: var(--font-family-general-sans, sans-serif);',
      '  font-size: var(--font-size-0, 9px);',
      '  font-weight: var(--font-weight-medium, 500);',
      '  left: var(--spacing-xs, 8px);',
      '  letter-spacing: var(--letter-spacing-1, 1px);',
      '  line-height: var(--line-height-0, 150%);',
      '  position: absolute;',
      '  text-transform: uppercase;',
      '  top: var(--spacing-xs, 8px);',
      '  z-index: var(--layer-flat, 1);',
      '}',
      '.jh-quick-view {',
      '  align-items: center;',
      '  background-color: transparent;',
      '  border: 0;',
      '  bottom: 0;',
      '  color: var(--color-brand-black, #000);',
      '  cursor: pointer;',
      '  display: flex;',
      '  font-family: var(--font-family-general-sans, sans-serif);',
      '  font-size: 22px;',
      '  font-weight: 300;',
      '  justify-content: center;',
      '  letter-spacing: 0.5px;',
      '  line-height: 1;',
      '  padding: 12px 16px;',
      '  position: absolute;',
      '  right: 0;',
      '  text-transform: uppercase;',
      '  transition: opacity var(--timing-normal, 0.3s) ease-in-out, background-color var(--timing-normal, 0.3s) ease-out, color var(--timing-normal, 0.3s) ease-out;',
      '  z-index: var(--layer-raised, 2);',
      '}',
      '[class*="bonsai-result-card"]:hover .jh-quick-view,',
      '.jh-quick-view:focus-visible {',
      '  opacity: 1;',
      '}',
      '.jh-quick-view:hover,',
      '.jh-quick-view:focus-visible {',
      '  background-color: var(--color-brand-black, #000);',
      '  color: var(--color-neutral-white, #fff);',
      '}',
      '.jh-quick-view__label {',
      '  display: none;',
      '}',
      /**
       * Wishlist King heart, matching the vendor overrides for product
       * cards; the app's own styles cannot reach into the shadow root.
       */
      'wishlist-button-collection {',
      '  margin: calc(-1 * var(--spacing-s, 12px));',
      '  position: absolute;',
      '  right: var(--spacing-xs, 8px);',
      '  top: var(--spacing-xs, 8px);',
      '  transition: opacity var(--timing-normal, 0.3s) var(--easing-normal, ease-out), visibility var(--timing-normal, 0.3s) var(--easing-normal, ease-out);',
      '  z-index: var(--layer-raised, 2);',
      '}',
      'wishlist-button-collection .wk-button {',
      '  align-items: center;',
      '  background: none;',
      '  border: 0;',
      '  cursor: pointer;',
      '  display: flex;',
      '  height: 48px;',
      '  justify-content: center;',
      '  padding: 0;',
      '  width: 48px;',
      '}',
      'wishlist-button-collection svg {',
      '  display: block;',
      '  height: 24px;',
      '  width: 24px;',
      '}',
      'wishlist-button-collection svg path {',
      '  fill: transparent;',
      '  transition: fill var(--timing-quick, 0.15s) var(--easing-normal, ease-out);',
      '}',
      'wishlist-button-collection .wk-selected svg path {',
      '  fill: var(--color-brand-black, #000);',
      '}',
      '@media (min-width: 1024px) {',
      '  wishlist-button-collection {',
      '    opacity: 0;',
      '    right: var(--spacing-m, 16px);',
      '    top: var(--spacing-m, 16px);',
      '    visibility: hidden;',
      '  }',
      '  [class*="bonsai-result-card"]:hover wishlist-button-collection,',
      '  [class*="bonsai-result-card"]:focus-within wishlist-button-collection {',
      '    opacity: 1;',
      '    visibility: visible;',
      '  }',
      '  .jh-badge {',
      '    background-color: var(--color-neutral-white, #fff);',
      '    left: var(--spacing-m, 16px);',
      '    padding: var(--spacing-2xs, 4px) var(--spacing-xs, 8px);',
      '    top: var(--spacing-m, 16px);',
      '  }',
      '  .jh-quick-view {',
      '    background-color: var(--color-neutral-white, #fff);',
      '    bottom: 10px;',
      '    font-size: 11px;',
      '    font-weight: var(--font-weight-medium, 500);',
      '    left: 14px;',
      '    opacity: 0;',
      '    padding: 9px 12px;',
      '    right: 14px;',
      '  }',
      '  .jh-quick-view__label {',
      '    display: block;',
      '  }',
      '  .jh-quick-view__plus {',
      '    display: none;',
      '  }',
      '}',
    ].join('\n')
    root.appendChild(style)
  }

  /**
   * Observe the component's results container so cards rendered by any later
   * pass (filter re-runs, load more) are also enhanced.
   * @param {Object} event - Bonsai `results` event.
   */
  function handleRender(event) {
    var element = event.target

    if (!element || !element.search || !element.search.resultsContainer) {
      return
    }

    var container = element.search.resultsContainer
    var results = (event.detail && event.detail.results) || []

    if (results.length) {
      containerBadges.set(container, buildBadgeMap(results))
    }

    containerWindows.set(container, RESULTS_WINDOW)
    enhanceCards(container)
    paintedSearches.add(element.search)
    placeLoadingState(element.search)
    showPanel(container.getRootNode().querySelector('.jh-filter-bar'), element.search)

    if (observedContainers.has(container)) {
      return
    }

    observedContainers.add(container)
    var observer = new MutationObserver(function () {
      enhanceCards(container)
    })
    observer.observe(container, { childList: true, subtree: true })
  }

  document.addEventListener('results', handleRender)

  /**
   * Reserve the suggestions dropdown's height on the drawer so the top
   * searches and banners sit below the list rather than behind it, which is
   * what makes the list read as part of the page instead of a dropdown; the
   * `is-suggesting` state also stands the hard-coded suggestions down while
   * the real list is open.
   * - The SDK renders the dropdown absolutely inside the search bar and hides
   *   it whenever a pointerdown lands outside that bar, so it cannot be moved
   *   into the flow without making the suggestions unclickable.
   * - The class observer catches the dropdown opening and closing, the size
   *   observer the overlay hiding and re-showing the drawer, which the class
   *   alone would miss as the dropdown keeps its class while hidden.
   * @param {Element} element - The <bonsai-search> element.
   */
  function trackSuggestions(element) {
    var dropdown = element.search && element.search.suggestionsDropdown
    var drawer = element.closest('.bonsai-search-drawer')

    if (!dropdown || !drawer) {
      return
    }

    var update = function () {
      var visible = dropdown.classList.contains('bonsai-visible')
      drawer.style.setProperty('--suggestions-height', visible ? dropdown.offsetHeight + 'px' : '')
      drawer.classList.toggle('is-suggesting', visible)
    }

    if (!observedDropdowns.has(dropdown)) {
      observedDropdowns.add(dropdown)

      var observer = new MutationObserver(update)
      observer.observe(dropdown, {
        attributeFilter: ['class'],
        attributes: true,
        childList: true,
        subtree: true,
      })

      new ResizeObserver(update).observe(dropdown)
    }

    update()
  }

  /**
   * Replace the clear button's icon with the theme's label, matching the
   * search drawer's own clear control.
   * @param {Element} element - The <bonsai-search> element.
   */
  function labelClearButton(element) {
    var button = element.search && element.search.clearBtn
    var text = label('clear')

    if (!button || button.textContent === text) {
      return
    }

    button.textContent = text
  }

  /**
   * Enhance the search bar the first time it is focused, the earliest point
   * the suggestions and clear button can show.
   * - `focusin` is composed, so it arrives here retargeted to the host.
   * @param {Object} event - Focus event from the search input.
   */
  function handleFocus(event) {
    var element = event.target

    if (!element || element.tagName !== 'BONSAI-SEARCH') {
      return
    }

    trackSuggestions(element)
    labelClearButton(element)
    watchSuggestionsFocus(element)
  }

  document.addEventListener('focusin', handleFocus)

  /**
   * Hide the suggestions once focus moves beyond the search bar — to the
   * filters, the results, or out of the component — as the SDK only closes
   * the list on an outside pointerdown, which leaves it hanging for
   * keyboard shoppers tabbing on.
   * - Listens inside the shadow tree, where `relatedTarget` is the real
   *   next element; from the document every move within the component
   *   retargets to the host and reads as internal.
   * @param {Element} element - The <bonsai-search> element.
   */
  function watchSuggestionsFocus(element) {
    var bar = element.search && element.search.searchBar

    if (!bar || bar.bonsaiJhFocusHide) {
      return
    }

    bar.bonsaiJhFocusHide = true

    bar.addEventListener('focusout', function (event) {
      if (event.relatedTarget && bar.contains(event.relatedTarget)) {
        return
      }

      var dropdown = element.search.suggestionsDropdown

      if (dropdown) {
        dropdown.classList.remove('bonsai-visible')
      }
    })
  }

  /**
   * Filter panel renderer matching the collection top bar's "Filter by:"
   * dropdown row. Renders into the component's closed shadow root, so styles
   * are carried inline; theme CSS custom properties still inherit through.
   * @param {Object} context - Bonsai filter panel render context.
   * @returns {Element}
   */
  window.bonsaiSearchFilterPanel = function (context) {
    var panel = document.createElement('div')
    panel.className = 'jh-filter-bar'
    panel.appendChild(buildStyles())

    var dimensions = context.dimensions.filter(function (dimension) {
      return dimension.type === 'keyword'
    })

    var inline = document.createElement('div')
    inline.className = 'jh-filter-bar__inline'
    panel.appendChild(inline)

    var groups = buildGroups(context, dimensions)

    if (groups.length) {
      var heading = document.createElement('span')
      heading.className = 'jh-filter-bar__label'
      heading.textContent = label('filter') + ':'
      inline.appendChild(heading)

      groups.forEach(function (group) {
        inline.appendChild(group)
      })
    }

    inline.appendChild(buildSort(context))

    var pills = buildPills(context)

    if (pills) {
      panel.appendChild(pills)
    }

    panel.appendChild(buildTriggers(context, dimensions))

    restoreTriggerFocus(panel)
    attachRootClose(panel)
    hideEmptyPanel(panel, context)
    return panel
  }

  /**
   * Build a pill for each selected filter value, which removes it when
   * clicked, matching the collection page's active filters.
   * - Sits between the two layouts: below the dropdowns on desktop, above the
   *   filter and sort buttons on mobile.
   * @param {Object} context - Bonsai filter panel render context.
   * @returns {Element|null} - Null when nothing is selected.
   */
  function buildPills(context) {
    var filters = context.filters || {}
    var row = document.createElement('div')
    row.className = 'jh-pills'

    Object.keys(filters).forEach(function (field) {
      var values = Array.isArray(filters[field]) ? filters[field] : []

      values.forEach(function (value) {
        row.appendChild(buildPill(context, field, value))
      })
    })

    return row.children.length ? row : null
  }

  /**
   * Build one selected filter's pill.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {String} field - Filter dimension field.
   * @param {String} value - Filter value.
   * @returns {Element}
   */
  function buildPill(context, field, value) {
    var pill = document.createElement('button')
    pill.type = 'button'
    pill.className = 'jh-pill'
    pill.setAttribute('aria-label', label('removeFilter').replace('{{ name }}', value))

    var text = document.createElement('span')
    text.textContent = String(value)

    var remove = document.createElement('span')
    remove.className = 'jh-pill__remove'
    remove.textContent = '\u2715'

    pill.appendChild(text)
    pill.appendChild(remove)

    pill.addEventListener('click', function () {
      toggleFilterValue(context, field, value)
    })

    return pill
  }

  /**
   * Build a dropdown for each filter dimension that has options to show.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {Array} dimensions - Keyword filter dimensions.
   * @returns {Array}
   */
  function buildGroups(context, dimensions, options) {
    var groups = []

    dimensions.forEach(function (dimension) {
      var group = buildGroup(context, dimension, options)

      if (group) {
        groups.push(group)
      }
    })

    return groups
  }

  /**
   * Build the mobile filter and sort row, where each button opens the choices
   * in a drawer rather than a dropdown, matching the collection top bar.
   * - The dropdowns are built a second time for the filter drawer as an
   *   element can only sit in one of the two layouts.
   * - Both drawers hold their choices until applied: running a search rebuilds
   *   this row, which would close the drawer the shopper is still using.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {Array} dimensions - Keyword filter dimensions.
   * @returns {Element}
   */
  function buildTriggers(context, dimensions) {
    var row = document.createElement('div')
    row.className = 'jh-filter-bar__triggers'

    var pending = { changed: false, filters: context.sdk.getFilters() || {} }

    var groups = buildGroups(context, dimensions, {
      onChange: function (field, value) {
        pending.changed = true
        toggleValue(pending.filters, field, value)
      },
    })

    if (groups.length) {
      var filters = document.createElement('div')
      filters.className = 'jh-drawer__filters'

      groups.forEach(function (group) {
        filters.appendChild(group)
      })

      row.appendChild(buildTrigger(label('filter'), filters, label('filterTitle'), {
        onApply: function () {
          if (!pending.changed) {
            return false
          }

          context.sdk.setFilters(pending.filters, { run: true })
          return true
        },
      }))
    }

    var options = buildSortOptions(context, { apply: false, name: 'jh-sort-drawer' })

    row.appendChild(buildTrigger(label('sort'), options.element, label('sortTitle'), {
      onApply: options.apply,
    }))

    return row
  }

  /**
   * Build one trigger button and the drawer it opens.
   * @param {String} label - Button label.
   * @param {Element} body - Drawer contents.
   * @param {String} title - Drawer title.
   * @param {Object} [options] - `onApply` runs before the drawer closes.
   * @returns {Element}
   */
  function buildTrigger(text, body, title, options) {
    var settings = options || {}

    var trigger = document.createElement('button')
    trigger.type = 'button'
    trigger.className = 'jh-trigger'
    trigger.textContent = text

    var drawer = document.createElement('div')
    drawer.className = 'jh-drawer'
    drawer.setAttribute('role', 'dialog')
    drawer.setAttribute('aria-modal', 'true')
    drawer.setAttribute('aria-label', title)

    var panel = document.createElement('div')
    panel.className = 'jh-drawer__panel'

    var header = document.createElement('div')
    header.className = 'jh-drawer__header'

    var close = document.createElement('button')
    close.type = 'button'
    close.className = 'jh-drawer__close'
    close.setAttribute('aria-label', label('close'))
    close.textContent = '\u2715'

    var heading = document.createElement('span')
    heading.className = 'jh-drawer__title'
    heading.textContent = title

    var apply = document.createElement('button')
    apply.type = 'button'
    apply.className = 'jh-drawer__apply'
    apply.textContent = label('apply')

    /**
     * Focus returns to the trigger on close, as the theme's focus handling
     * cannot see the drawer inside the closed shadow root and would
     * otherwise leave focus at the start of the component.
     */
    var setOpen = function (open) {
      drawer.classList.toggle('is-open', open)
      trigger.setAttribute('aria-expanded', String(open))
      clearDrawerPath(drawer, open)

      if (open) {
        close.focus()
      } else {
        trigger.focus()
      }
    }

    trigger.addEventListener('click', function (event) {
      event.stopPropagation()
      setOpen(true)
    })

    close.addEventListener('click', function () {
      setOpen(false)
    })

    drawer.addEventListener('click', function (event) {
      if (event.target === drawer) {
        setOpen(false)
      }
    })

    /**
     * Applying re-runs the search, which rebuilds the panel and replaces the
     * trigger focused on close, so the rebuilt one is remembered to focus.
     */
    apply.addEventListener('click', function () {
      setOpen(false)

      if (settings.onApply && settings.onApply()) {
        pendingTriggerFocus = text
      }
    })

    body.classList.add('jh-drawer__body')
    header.appendChild(close)
    header.appendChild(heading)
    panel.appendChild(header)
    panel.appendChild(body)
    panel.appendChild(apply)
    drawer.appendChild(panel)

    var wrapper = document.createElement('div')
    wrapper.appendChild(trigger)
    wrapper.appendChild(drawer)
    return wrapper
  }

  /**
   * Sort options, matching the values the search API accepts.
   * - Labels come from the theme's locales via the drawer.
   */
  var SORT_OPTIONS = ['relevance', 'price_asc', 'price_desc']

  /**
   * Build the sort control at the end of the filter row, matching the
   * collection top bar's sort dropdown.
   * - The SDK renders its own control above the results rather than in the
   *   filter row, so it stays off and the theme sets the sort the same way
   *   that control does.
   * @param {Object} context - Bonsai filter panel render context.
   * @returns {Element}
   */
  function buildSort(context) {
    var current = currentSort(context)

    var wrapper = document.createElement('div')
    wrapper.className = 'jh-filter-bar__sort'

    var heading = document.createElement('span')
    heading.className = 'jh-filter-bar__label'
    heading.textContent = label('sort') + ':'

    var group = document.createElement('div')
    group.className = 'jh-filter jh-filter--sort'

    var toggle = document.createElement('button')
    toggle.type = 'button'
    toggle.className = 'jh-filter__toggle'
    toggle.setAttribute('aria-expanded', 'false')
    toggle.textContent = sortLabel(current)

    var caret = document.createElement('span')
    caret.className = 'jh-filter__caret'
    toggle.appendChild(caret)

    var dropdown = buildSortOptions(context, { apply: true, name: 'jh-sort' }).element
    dropdown.classList.add('jh-filter__dropdown')

    toggle.addEventListener('click', function (event) {
      event.stopPropagation()
      var isOpen = group.classList.toggle('is-open')
      toggle.setAttribute('aria-expanded', String(isOpen))
      closeGroups(group.closest('.jh-filter-bar'), group)
    })

    group.appendChild(toggle)
    group.appendChild(dropdown)
    wrapper.appendChild(heading)
    wrapper.appendChild(group)
    return wrapper
  }

  /**
   * The order the results are currently in.
   * @param {Object} context - Bonsai filter panel render context.
   * @returns {String}
   */
  function currentSort(context) {
    return (context.sdk && context.sdk.currentSort) || 'relevance'
  }

  /**
   * Build the list of sort options.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {Object} options - `apply` sorts on choosing rather than on the
   *                           drawer's apply button, `name` groups the radios.
   * @returns {Object} - The list, and an apply function for the drawer.
   */
  function buildSortOptions(context, options) {
    var pending = currentSort(context)

    var list = document.createElement('div')
    list.className = 'jh-sort-options'

    SORT_OPTIONS.forEach(function (value) {
      var option = document.createElement('label')
      option.className = 'jh-filter__option'

      var input = document.createElement('input')
      input.type = 'radio'
      input.name = options.name
      input.checked = value === pending
      input.addEventListener('change', function () {
        pending = value

        if (options.apply) {
          applySort(context.sdk, value)
        }
      })

      var text = document.createElement('span')
      text.textContent = sortLabel(value)

      option.appendChild(input)
      option.appendChild(text)
      list.appendChild(option)
    })

    return {
      apply: function () {
        return applySort(context.sdk, pending)
      },
      element: list,
    }
  }

  /**
   * Re-run the search with a new sort order.
   * - Mirrors the SDK's own sort control: the order is held on the instance
   *   and applied by re-running the current query and filters.
   * @param {Object} sdk - The component's inner search instance.
   * @param {String} value - Sort option value.
   */
  function applySort(sdk, value) {
    if (!sdk || sdk.currentSort === value) {
      return false
    }

    sdk.currentSort = value
    sdk.runWithCurrentFilters(sdk.inBrowseMode())
    return true
  }

  /**
   * Hide the whole panel until a search has been made and its results are in,
   * and when no dropdowns rendered, matching collection filters where
   * zero-count options are not shown.
   * - The SDK keeps the dimensions it has already fetched when the search is
   *   reset, so without the submitted check the row would come back before
   *   the next search rather than only after it.
   * - The row arrives with the first cards and then stays, so that filtering
   *   and sorting refine the results in place rather than appearing to start
   *   again; it only goes away when the search is reset.
   * @param {Element} panel - The filter bar element.
   * @param {Object} context - Bonsai filter panel render context.
   */
  function hideEmptyPanel(panel, context) {
    setTimeout(function () {
      placeLoadingState(context.sdk)
      showPanel(panel, context.sdk)
      clearDrawerPath(panel, false)
    })
  }

  /**
   * Clear the search bar out of a drawer's way while it covers the page.
   * - The drawer renders inside the filter row, so it cannot rise above the
   *   sticky bar on its own; the row's own z-index caps everything in it.
   * - The bar is hidden as well as out-ranked, as it sticks to the scroll
   *   position rather than sitting in the flow the row can lift it out of.
   * - The theme's search icon sits outside the component altogether, layered
   *   over it, so it is hidden from the drawer the component renders in.
   * @param {Element} node - Any element in the component's shadow tree.
   * @param {Boolean} open - Whether a drawer is open.
   */
  function clearDrawerPath(node, open) {
    var root = node.getRootNode()

    if (!root.querySelector) {
      return
    }

    var panel = root.querySelector('[class*="bonsai-filter-panel"]')
    var bar = root.querySelector('[class*="bonsai-search-bar"]')
    var host = root.host && root.host.closest('.bonsai-search-drawer')
    var icon = host && host.querySelector('.bonsai-search-drawer__search-icon')

    if (panel) {
      panel.style.zIndex = open ? 'var(--layer-overlay, 16)' : ''
    }

    if (bar) {
      bar.style.visibility = open ? 'hidden' : ''
    }

    if (icon) {
      icon.style.visibility = open ? 'hidden' : ''
    }
  }

  /**
   * Show or hide one filter row for the state its search is in.
   * @param {Element} panel - The filter bar element.
   * @param {Object} sdk - The component's inner search instance.
   */
  function showPanel(panel, sdk) {
    var host = panel && panel.parentElement

    if (!host || !sdk) {
      return
    }

    var painted = paintedSearches.has(sdk)
    var ready = sdk.hasSubmittedSearch && (painted || sdk.state !== 'loading')
    host.style.display = ready && panel.querySelector('.jh-filter') ? '' : 'none'
  }

  /**
   * Move the loading state below the filter row, so the row holds its place
   * and the results swap in underneath it.
   * - The SDK renders the loading state above the row; it is only ever
   *   addressed by reference, so it can be moved without breaking it.
   * @param {Object} sdk - The component's inner search instance.
   */
  function placeLoadingState(sdk) {
    var panel = sdk && sdk.filterPanel
    var loading = sdk && sdk.loadingState

    if (!panel || !loading || loading.previousElementSibling === panel) {
      return
    }

    panel.after(loading)

    if (sdk.loadingThumbs) {
      loading.after(sdk.loadingThumbs)
    }
  }

  /**
   * Build one filter dropdown.
   * - Options with a zero count are hidden to match collection filters,
   *   unless currently selected.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {Object} dimension - Filter dimension with field and values.
   * @returns {Element|null}
   */
  function buildGroup(context, dimension, options) {
    var settings = options || {}
    var selected = context.filters && context.filters[dimension.field]
    var selectedValues = Array.isArray(selected) ? selected : []

    var values = (dimension.values || []).filter(function (raw) {
      var value = raw && typeof raw === 'object' ? raw.value : raw
      var count = raw && typeof raw === 'object' ? raw.count : null

      return count !== 0 || selectedValues.indexOf(value) > -1
    })

    if (!values.length) {
      return null
    }

    var group = document.createElement('div')
    group.className = 'jh-filter'

    var toggle = document.createElement('button')
    toggle.type = 'button'
    toggle.className = 'jh-filter__toggle'
    toggle.setAttribute('aria-expanded', 'false')
    toggle.textContent = groupLabel(dimension)

    var caret = document.createElement('span')
    caret.className = 'jh-filter__caret'
    toggle.appendChild(caret)

    var dropdown = document.createElement('div')
    dropdown.className = 'jh-filter__dropdown'

    values.forEach(function (raw) {
      var value = raw && typeof raw === 'object' ? raw.value : raw
      var count = raw && typeof raw === 'object' ? raw.count : null

      var option = document.createElement('label')
      option.className = 'jh-filter__option'

      var input = document.createElement('input')
      input.type = 'checkbox'
      input.checked = selectedValues.indexOf(value) > -1
      input.addEventListener('change', function () {
        if (settings.onChange) {
          settings.onChange(dimension.field, value)
          return
        }

        toggleFilterValue(context, dimension.field, value)
      })

      var text = document.createElement('span')
      text.textContent = String(value)

      option.appendChild(input)
      option.appendChild(text)
      dropdown.appendChild(option)
    })

    toggle.addEventListener('click', function (event) {
      event.stopPropagation()
      var isOpen = group.classList.toggle('is-open')
      toggle.setAttribute('aria-expanded', String(isOpen))
      closeGroups(group.closest('.jh-filter-bar'), group)
    })

    group.appendChild(toggle)
    group.appendChild(dropdown)
    return group
  }

  /**
   * Toggle a filter value and re-run the search.
   * @param {Object} context - Bonsai filter panel render context.
   * @param {String} field - Filter dimension field.
   * @param {String|Boolean} value - Value to toggle.
   */
  function toggleFilterValue(context, field, value) {
    var filters = context.sdk.getFilters() || {}
    toggleValue(filters, field, value)
    context.sdk.setFilters(filters, { run: true })
  }

  /**
   * Add or remove one value from a set of filters.
   * @param {Object} filters - Filters to change.
   * @param {String} field - Filter dimension field.
   * @param {String} value - Filter value.
   */
  function toggleValue(filters, field, value) {
    var current = Array.isArray(filters[field]) ? filters[field].slice() : []
    var index = current.indexOf(value)

    if (index > -1) {
      current.splice(index, 1)
    } else {
      current.push(value)
    }

    if (current.length) {
      filters[field] = current
      return
    }

    delete filters[field]
  }

  /**
   * Close open dropdowns, optionally keeping one.
   * @param {Element} panel - The filter bar element.
   * @param {Element} [keep] - Group to leave open.
   */
  function closeGroups(panel, keep) {
    if (!panel) {
      return
    }

    panel.querySelectorAll('.jh-filter.is-open').forEach(function (group) {
      if (group === keep) {
        return
      }

      group.classList.remove('is-open')
      group.querySelector('.jh-filter__toggle').setAttribute('aria-expanded', 'false')
    })
  }

  /**
   * Focus the rebuilt trigger once the re-rendered panel is attached, so a
   * keyboard shopper's apply lands them back on the button they came from.
   * - Held through the settle window rather than set once, as the panel is
   *   built before the SDK shows it, and the theme's focus trap re-focuses
   *   its own first element on a timer after the results update, which
   *   would take the focus straight back.
   * - Stands down at the shopper's next press or tap so it never fights
   *   real input, and after a second and a half in any case.
   * @param {Element} panel - The freshly built filter panel.
   */
  function restoreTriggerFocus(panel) {
    if (!pendingTriggerFocus) {
      return
    }

    var text = pendingTriggerFocus
    var attempts = 0
    var cancelled = false

    var cancel = function () {
      cancelled = true
    }

    window.addEventListener('keydown', cancel, { capture: true, once: true })
    window.addEventListener('pointerdown', cancel, { capture: true, once: true })

    var cleanup = function () {
      window.removeEventListener('keydown', cancel, true)
      window.removeEventListener('pointerdown', cancel, true)
    }

    var attempt = function () {
      if (cancelled || pendingTriggerFocus !== text) {
        cleanup()
        return
      }

      attempts += 1

      if (attempts > 30) {
        pendingTriggerFocus = null
        cleanup()
        return
      }

      var triggers = panel.querySelectorAll('.jh-trigger')

      for (var index = 0; index < triggers.length; index += 1) {
        var trigger = triggers[index]

        if (trigger.textContent === text && trigger.offsetParent !== null) {
          if (panel.getRootNode().activeElement !== trigger) {
            trigger.focus()
          }

          break
        }
      }

      setTimeout(attempt, 50)
    }

    setTimeout(attempt)
  }

  /**
   * Close dropdowns on any click outside them, listening on the shadow root
   * so retargeting doesn't hide the real click target, and register the root
   * with the Escape handling below.
   * @param {Element} panel - The filter bar element.
   */
  function attachRootClose(panel) {
    setTimeout(function () {
      var root = panel.getRootNode()

      if (!root || root.bonsaiJhFilterClose) {
        return
      }

      root.bonsaiJhFilterClose = true
      filterRoots.push(root)
      root.addEventListener('click', function (event) {
        var target = event.target
        var inFilter = target.closest && target.closest('.jh-filter')
        closeGroups(root.querySelector('.jh-filter-bar'), inFilter)
      })
    })
  }

  /**
   * Close an open filter drawer or dropdown on Escape and keep the key from
   * the theme, so the search overlay itself only closes once nothing in the
   * filter bar is left open.
   * - Captured on the window as focus can sit outside the closed shadow root,
   *   where the theme's own window listener would otherwise act first.
   * @param {Object} event - Keydown event.
   */
  function handleEscape(event) {
    if (event.key !== 'Escape') {
      return
    }

    filterRoots.forEach(function (root) {
      var drawer = root.querySelector('.jh-drawer.is-open')
      var group = root.querySelector('.jh-filter.is-open')

      if (drawer) {
        drawer.querySelector('.jh-drawer__close').click()
      } else if (group) {
        closeGroups(root.querySelector('.jh-filter-bar'))
      } else {
        return
      }

      event.stopPropagation()
    })
  }

  window.addEventListener('keydown', handleEscape, true)

  /**
   * Trap Tab inside an open filter or sort drawer, as the theme's focus trap
   * cannot see into the closed shadow root and the browser would otherwise
   * walk the results and the page behind the overlay.
   * - The theme's own handler still runs; it stands down while focus sits in
   *   a web component, so only this trap moves focus.
   * @param {Object} event - Keydown event.
   */
  function handleTrapTab(event) {
    if (event.key !== 'Tab') {
      return
    }

    filterRoots.forEach(function (root) {
      var drawer = root.querySelector('.jh-drawer.is-open')

      if (!drawer) {
        return
      }

      var focusables = []

      drawer.querySelectorAll('button, input, select, textarea, a[href]').forEach(function (element) {
        if (!element.disabled && element.offsetParent !== null) {
          focusables.push(element)
        }
      })

      if (!focusables.length) {
        return
      }

      var index = focusables.indexOf(root.activeElement)
      var next = index + (event.shiftKey ? -1 : 1)

      if (index === -1) {
        next = 0
      } else if (next < 0) {
        next = focusables.length - 1
      } else if (next >= focusables.length) {
        next = 0
      }

      event.preventDefault()
      focusables[next].focus()
    })
  }

  window.addEventListener('keydown', handleTrapTab, true)

  /**
   * Human label for a filter dimension, e.g. "stone_type" > "Stone Type".
   * @param {Object} dimension - Filter dimension.
   * @returns {String}
   */
  function groupLabel(dimension) {
    if (dimension.label) {
      return dimension.label
    }

    return dimension.field
      .replace(/^tag:/i, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\b[a-z]/g, function (letter) {
        return letter.toUpperCase()
      })
  }

  /**
   * Styles for the filter bar, scoped inside the component's shadow root.
   * @returns {Element}
   */
  function buildStyles() {
    var style = document.createElement('style')
    style.textContent = [
      '.jh-filter-bar, .jh-filter-bar * {',
      '  box-sizing: border-box;',
      '}',
      '.jh-filter-bar {',
      '  font-family: var(--font-family-general-sans, sans-serif);',
      '  padding-block: 0 8px;',
      '}',
      '.jh-filter-bar__inline {',
      '  align-items: baseline;',
      '  display: none;',
      '  flex-wrap: wrap;',
      '  gap: 8px 32px;',
      '}',
      '.jh-filter-bar__label {',
      '  color: var(--color-neutral-dark-grey, #646464);',
      '  font-size: 13px;',
      '  letter-spacing: 0.5px;',
      '}',
      '.jh-filter-bar__sort {',
      '  align-items: baseline;',
      '  display: flex;',
      '  gap: 12px;',
      '  margin-inline-start: auto;',
      '}',
      '.jh-filter {',
      '  position: relative;',
      '}',
      '.jh-filter__toggle {',
      '  align-items: center;',
      '  background: none;',
      '  border: 0;',
      '  color: var(--color-neutral-charcoal, #33383c);',
      '  cursor: pointer;',
      '  display: flex;',
      '  font-family: inherit;',
      '  font-size: 13px;',
      '  gap: 10px;',
      '  letter-spacing: 0.5px;',
      '  padding: 0;',
      '}',
      '.jh-filter__caret {',
      '  border-block-end: 1px solid currentcolor;',
      '  border-inline-end: 1px solid currentcolor;',
      '  height: 5px;',
      '  margin-block-start: -3px;',
      '  transform: rotate(45deg);',
      '  transition: transform var(--timing-quick, 0.15s) ease-out;',
      '  width: 5px;',
      '}',
      '.jh-filter.is-open .jh-filter__caret {',
      '  margin-block-start: 3px;',
      '  transform: rotate(225deg);',
      '}',
      '.jh-filter__dropdown {',
      '  background-color: var(--color-neutral-white, #fff);',
      '  box-shadow: 0 8px 15px 0 rgb(0 0 0 / 8%);',
      '  display: none;',
      '  left: 0;',
      '  max-height: 320px;',
      '  min-width: 250px;',
      '  overflow-y: auto;',
      '  padding: 20px;',
      '  position: absolute;',
      '  top: calc(100% + 12px);',
      '  z-index: var(--layer-heightened, 4);',
      '}',
      '.jh-filter--sort .jh-filter__dropdown {',
      '  left: auto;',
      '  right: 0;',
      '}',
      '.jh-filter.is-open .jh-filter__dropdown {',
      '  display: flex;',
      '  flex-direction: column;',
      '  gap: 14px;',
      '}',
      '.jh-filter__option {',
      '  align-items: center;',
      '  color: var(--color-neutral-charcoal, #33383c);',
      '  cursor: pointer;',
      '  display: flex;',
      '  font-size: 13px;',
      '  gap: 12px;',
      '  letter-spacing: 0.5px;',
      '  white-space: nowrap;',
      '}',
      '.jh-pills {',
      '  display: flex;',
      '  flex-wrap: wrap;',
      '  gap: 8px;',
      '  padding-block: 0 12px;',
      '}',
      '.jh-pill {',
      '  align-items: center;',
      '  background-color: var(--color-neutral-black, #000);',
      '  border: 0;',
      '  color: var(--color-neutral-white, #fff);',
      '  cursor: pointer;',
      '  display: flex;',
      '  font-family: inherit;',
      '  font-size: 13px;',
      '  gap: 12px;',
      '  letter-spacing: 0.5px;',
      '  padding: 7px 10px;',
      '}',
      '.jh-pill__remove {',
      '  font-size: 11px;',
      '  line-height: 1;',
      '}',
      /**
       * Mobile: the choices move into drawers behind a pair of buttons,
       * matching the collection top bar.
       */
      '.jh-filter-bar__triggers {',
      '  display: grid;',
      '  gap: 8px;',
      '  grid-auto-columns: 1fr;',
      '  grid-auto-flow: column;',
      '}',
      '.jh-trigger {',
      '  background: none;',
      '  border: 1px solid var(--color-neutral-light-grey, #e6e6e6);',
      '  color: var(--color-neutral-dark-grey, #646464);',
      '  cursor: pointer;',
      '  font-family: inherit;',
      '  font-size: 11px;',
      '  font-weight: var(--font-weight-medium, 500);',
      '  letter-spacing: 1px;',
      '  padding: 14px 8px;',
      '  text-transform: uppercase;',
      '  width: 100%;',
      '}',
      /**
       * The scrim fades and the panel slides in from the left, matching the
       * theme's drawer overlay transitions; visibility keeps the closed
       * drawer out of the tab order while still letting the close animation
       * play.
       */
      '.jh-drawer {',
      '  background-color: var(--color-overlay, rgb(0 0 0 / 30%));',
      '  inset: 0;',
      '  opacity: 0;',
      '  position: fixed;',
      '  transition: opacity var(--timing-normal, 0.3s) var(--easing-normal, ease-out), visibility var(--timing-normal, 0.3s) var(--easing-normal, ease-out);',
      '  visibility: hidden;',
      '  z-index: var(--layer-window-overlay, 12);',
      '}',
      '.jh-drawer.is-open {',
      '  opacity: 1;',
      '  visibility: visible;',
      '}',
      '.jh-drawer__panel {',
      '  background-color: var(--color-neutral-white, #fff);',
      '  display: flex;',
      '  flex-direction: column;',
      '  height: 100%;',
      '  max-width: 360px;',
      '  transition: translate var(--timing-normal, 0.3s) var(--easing-normal, ease-out);',
      '  translate: -100% 0;',
      '  width: 86%;',
      '}',
      '.jh-drawer.is-open .jh-drawer__panel {',
      '  translate: 0;',
      '}',
      '.jh-drawer__header {',
      '  align-items: center;',
      '  display: flex;',
      '  padding: 24px 16px;',
      '}',
      '.jh-drawer__close {',
      '  background: none;',
      '  border: 0;',
      '  color: var(--color-neutral-black, #000);',
      '  cursor: pointer;',
      '  font-family: inherit;',
      '  font-size: 20px;',
      '  line-height: 1;',
      '  padding: 0;',
      '}',
      '.jh-drawer__title {',
      '  color: var(--color-neutral-black, #000);',
      '  flex: 1;',
      '  font-size: 15px;',
      '  margin-inline-end: 20px;',
      '  text-align: center;',
      '}',
      '.jh-drawer__body {',
      '  display: flex;',
      '  flex: 1;',
      '  flex-direction: column;',
      '  gap: 20px;',
      '  overflow-y: auto;',
      '  padding: 8px 16px 24px;',
      '}',
      '.jh-drawer__apply {',
      '  background-color: var(--color-neutral-black, #000);',
      '  border: 0;',
      '  color: var(--color-neutral-white, #fff);',
      '  cursor: pointer;',
      '  font-family: inherit;',
      '  font-size: 11px;',
      '  font-weight: var(--font-weight-medium, 500);',
      '  letter-spacing: 1px;',
      '  margin: 16px;',
      '  padding: 16px;',
      '  text-transform: uppercase;',
      '}',
      /**
       * Filter dropdowns become accordions once inside the drawer.
       */
      '.jh-drawer__filters .jh-filter__dropdown {',
      '  box-shadow: none;',
      '  max-height: none;',
      '  min-width: 0;',
      '  padding: 12px 0 0;',
      '  position: static;',
      '}',
      '.jh-filter__option input {',
      '  accent-color: var(--color-neutral-black, #000);',
      '  flex: 0 0 16px;',
      '  height: 16px;',
      '  margin: 0;',
      '  width: 16px;',
      '}',
      /**
       * Desktop: the dropdown row shows and the trigger buttons stand down.
       */
      '@media (min-width: 1024px) {',
      '  .jh-filter-bar {',
      '    padding-block: 16px;',
      '  }',
      '  .jh-filter-bar__inline {',
      '    display: flex;',
      '  }',
      '  .jh-filter-bar__triggers {',
      '    display: none;',
      '  }',
      '  .jh-pills {',
      '    padding-block: 12px 0;',
      '  }',
      '}',
    ].join('\n')
    return style
  }
})()
